import React from "react";
import { motion } from "framer-motion";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Box,
  Button,
  IconButton,
  Flex,
  Heading,
  Text,
  useToast,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  HStack,
  Badge,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  useDisclosure,
  useColorModeValue,
  VStack,
} from "@chakra-ui/react";
import {
  EditIcon,
  DeleteIcon,
  TriangleDownIcon,
  TriangleUpIcon,
  ViewIcon,
} from "@chakra-ui/icons";

import { FiFilter, FiMoreVertical, FiInbox } from "react-icons/fi";
import { CustomIcon } from "./CustomIcon";
import { Pagination } from "./Pagination";
import { PersianDatePicker } from "./common/PersianDatePicker";
import {
  TableFilterDefinition,
  TableFilterValues,
  TableSortDirection,
  TableSortState,
} from "../types";
import { toGregorianIsoDate, toPersianDayValue } from "../utils/persianDate";

const normalizeNumericFilterValue = (value: string) =>
  value
    .replace(/[۰-۹]/g, (digit) => String("۰۱۲۳۴۵۶۷۸۹".indexOf(digit)))
    .replace(/[٠-٩]/g, (digit) => String("٠١٢٣٤٥٦٧٨٩".indexOf(digit)))
    .replace(/[^\d]/g, "");

const formatNumericFilterValue = (value?: string) => {
  const digits = normalizeNumericFilterValue(String(value || ""));
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

export interface Column {
  header: string;
  accessor: string;
  isSortable?: boolean;
  type?: "text" | "status" | "date" | "actions";
  render?: (item: any) => React.ReactNode;
}

interface DataTableProps {
  columns: Column[];
  data: any[];
  title: string;
  showAddBtn?: boolean;
  canExport?: boolean;
  isExporting?: boolean;
  totalData: number;
  currentPage: number;
  pageSize: number;
  totalPage?: number;
  onPageChange: (page: number) => void;
  onAdd?: (item: any) => void;
  onExport?: () => void;
  onView?: (item: any) => void;
  onEdit?: (item: any) => void;
  onDelete?: (item: any) => void;
  sort?: TableSortState;
  filterDefinitions?: TableFilterDefinition[];
  filterValues?: TableFilterValues;
  onSort?: (sort: TableSortState) => void;
  onFiltersChange?: (filters: TableFilterValues) => void;
  canAdd?: boolean;
  canView?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
}

export const DataTable: React.FC<DataTableProps> = ({
  title,
  columns,
  data,
  showAddBtn = true,
  canExport = true,
  isExporting = false,
  totalData,
  currentPage,
  pageSize,
  totalPage,
  onPageChange,
  onAdd,
  onExport,
  onView,
  onEdit,
  onDelete,
  sort,
  filterDefinitions = [],
  filterValues = {},
  onSort,
  onFiltersChange,
  canAdd = true,
  canView = true,
  canEdit = true,
  canDelete = true,
}) => {
  // Modal
  const toast = useToast();
  const filterModal = useDisclosure();

  // Theme
  const tableContainerBorderColor = useColorModeValue("gray.200", "gray.700");
  const tableBgColor = useColorModeValue("light.700", "dark.700");
  const tableBgColorSecondary = useColorModeValue("light.200", "dark.200");
  const emptyStateColor = useColorModeValue("gray.400", "gray.600");

  const [draftFilters, setDraftFilters] =
    React.useState<TableFilterValues>(filterValues);
  const activeFilterCount = Object.values(filterValues).filter(
    (value) => String(value).trim() !== "",
  ).length;

  const handleSort = (accessor: string) => {
    const direction: TableSortDirection =
      sort?.field === accessor && sort.direction === "asc" ? "desc" : "asc";
    onSort?.({ field: accessor, direction });
  };

  const handleOpenFilters = () => {
    setDraftFilters(filterValues);
    filterModal.onOpen();
  };

  const handleApplyFilters = () => {
    const normalizedFilters = Object.fromEntries(
      Object.entries(draftFilters).filter(
        ([, value]) => String(value).trim() !== "",
      ),
    );
    onFiltersChange?.(normalizedFilters);
    filterModal.onClose();
  };

  const handleClearFilters = () => {
    setDraftFilters({});
    onFiltersChange?.({});
    filterModal.onClose();
  };

  const handleDelete = async (item: any) => {
    try {
      if (onDelete) {
        await onDelete(item);
      }
    } catch (error) {
      toast({
        title: "خطا در حذف آیتم",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const getStatusBadge = (status: string) => {
    const statusColors: Record<string, string> = {
      pending: "yellow",
      approved: "green",
      declined: "red",
      closed: "gray",
    };

    return (
      <Badge
        colorScheme={statusColors[status.toLowerCase()] || "gray"}
        px={2}
        py={1}
        borderRadius="full"
      >
        {status}
      </Badge>
    );
  };

  return (
    <>
      <Flex
        flexDir={"column"}
        w="100%"
        h="100%"
        overflowY={"hidden"}
        borderRadius={"2xl"}
        border="1px solid"
        borderColor={tableContainerBorderColor}
      >
        <Box
          overflowY={"auto"}
          w="100%"
          h={"100%"}
          display="flex"
          flexDirection="column"
          justifyContent={"space-between"}
        >
          <Flex flexDir={"column"} w="100%" h="100%">
            <Flex
              w="100%"
              justify="space-between"
              align="center"
              bg={tableBgColorSecondary}
              borderBottom="1px solid"
              borderColor={tableContainerBorderColor}
              px={5}
              py={4}
            >
              <Flex flexDir={"column"}>
                <Heading size="md" mb={2}>
                  {title}
                </Heading>
                <Text fontSize="sm">
                  نمایش {totalData > 0 ? (currentPage - 1) * pageSize + 1 : 0}{" "}
                  تا {Math.min(currentPage * pageSize, totalData)} رکورد از{" "}
                  {totalData} رکورد
                </Text>
              </Flex>
              <HStack spacing={3}>
                {filterDefinitions.length > 0 && onFiltersChange && (
                  <Button
                    borderRadius="full"
                    fontSize="sm"
                    variant="outline"
                    leftIcon={<CustomIcon icon={FiFilter} />}
                    onClick={handleOpenFilters}
                  >
                    <HStack spacing={2}>
                      <Text>فیلترها</Text>
                      {activeFilterCount > 0 && (
                        <Badge colorScheme="brand" borderRadius="full">
                          {activeFilterCount.toLocaleString("fa-IR")}
                        </Badge>
                      )}
                    </HStack>
                  </Button>
                )}
                {onExport && canExport && (
                  <Button
                    borderRadius={"full"}
                    fontSize={"sm"}
                    variant="outline"
                    onClick={onExport}
                    isLoading={isExporting}
                    loadingText="در حال آماده‌سازی"
                  >
                    دانلود اکسل
                  </Button>
                )}
                {showAddBtn && onAdd && canAdd && (
                  <Button borderRadius={"full"} fontSize={"sm"} onClick={onAdd}>
                    افزودن
                  </Button>
                )}
              </HStack>
            </Flex>

            <Table w="100%">
              <Thead
                bg={tableBgColor}
                h="60px"
                position="sticky"
                top={0}
                zIndex={2}
              >
                <Tr>
                  {columns.map((column) => {
                    return (
                      <Th
                        key={column.accessor}
                        onClick={() =>
                          column.isSortable && handleSort(column.accessor)
                        }
                        cursor={column.isSortable ? "pointer" : "default"}
                        position="relative"
                        py={2}
                      >
                        <HStack spacing={2}>
                          <Text>{column.header}</Text>
                          {column.isSortable &&
                            (sort?.field === column.accessor ? (
                              sort.direction === "asc" ? (
                                <TriangleUpIcon />
                              ) : (
                                <TriangleDownIcon />
                              )
                            ) : (
                              <TriangleDownIcon opacity={0.3} />
                            ))}
                        </HStack>
                      </Th>
                    );
                  })}
                  {((onView && canView) ||
                    (onEdit && canEdit) ||
                    (onDelete && canDelete)) && <Th width="60px">اقدامات</Th>}
                </Tr>
              </Thead>
              <Tbody>
                {data.length === 0 ? (
                  <Tr>
                    <Td
                      colSpan={
                        columns.length +
                        ((onView && canView) ||
                        (onEdit && canEdit) ||
                        (onDelete && canDelete)
                          ? 1
                          : 0)
                      }
                      py={12}
                    >
                      <VStack spacing={3}>
                        <CustomIcon icon={FiInbox} size={48} />
                        <Text color={emptyStateColor}>
                          {activeFilterCount > 0
                            ? "نتیجه‌ای مطابق فیلترهای انتخاب‌شده پیدا نشد"
                            : "داده‌ای برای نمایش وجود ندارد"}
                        </Text>
                      </VStack>
                    </Td>
                  </Tr>
                ) : (
                  data.map((item, index) => (
                    <motion.tr
                      key={item?._id || index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.07 }}
                      style={{ display: "table-row" }}
                    >
                      {columns.map((column) => (
                        <Td key={`${index}-${column.accessor}`} py={2}>
                          {column.render
                            ? column.render(item)
                            : column.type === "status"
                              ? getStatusBadge(item[column.accessor])
                              : item[column.accessor]}
                        </Td>
                      ))}
                      {((onView && canView) ||
                        (onEdit && canEdit) ||
                        (onDelete && canDelete)) && (
                        <Td py={2}>
                          <Menu>
                            <MenuButton
                              as={IconButton}
                              icon={<CustomIcon icon={FiMoreVertical} />}
                              variant="ghost"
                              size="sm"
                              aria-label="اقدامات"
                            />
                            <MenuList>
                              {onView && canView && (
                                <MenuItem
                                  icon={<ViewIcon />}
                                  onClick={() => onView(item)}
                                >
                                  مشاهده
                                </MenuItem>
                              )}
                              {onEdit && canEdit && (
                                <MenuItem
                                  icon={<EditIcon />}
                                  onClick={() => onEdit(item)}
                                >
                                  ویرایش
                                </MenuItem>
                              )}
                              {onDelete && canDelete && (
                                <MenuItem
                                  icon={<DeleteIcon />}
                                  onClick={() => handleDelete(item)}
                                  color="red.500"
                                >
                                  حذف
                                </MenuItem>
                              )}
                            </MenuList>
                          </Menu>
                        </Td>
                      )}
                    </motion.tr>
                  ))
                )}
              </Tbody>
            </Table>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPage || 1}
              onPageChange={onPageChange}
            />
          </Flex>
        </Box>
      </Flex>

      <Modal
        isOpen={filterModal.isOpen}
        onClose={filterModal.onClose}
        size="xl"
      >
        <ModalOverlay />
        <ModalContent dir="rtl" overflow="visible">
          <ModalHeader>فیلترها</ModalHeader>
          <ModalCloseButton left={4} right="auto" aria-label="بستن" />
          <ModalBody overflow="visible">
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              {filterDefinitions.map((filter) => (
                <FormControl key={filter.key}>
                  <FormLabel>{filter.label}</FormLabel>
                  {filter.type === "select" ? (
                    <Select
                      value={draftFilters[filter.key] || ""}
                      placeholder={filter.placeholder || "همه"}
                      textAlign="center"
                      sx={{ option: { textAlign: "center" } }}
                      onChange={(event) =>
                        setDraftFilters((current) => ({
                          ...current,
                          [filter.key]: event.target.value,
                        }))
                      }
                    >
                      {(filter.options || []).map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  ) : filter.type === "date" &&
                    filter.usePersianDatePicker ? (
                    <PersianDatePicker
                      value={toPersianDayValue(draftFilters[filter.key])}
                      placeholder={filter.placeholder}
                      name={filter.key}
                      onChange={(value) =>
                        setDraftFilters((current) => ({
                          ...current,
                          [filter.key]: toGregorianIsoDate(value),
                        }))
                      }
                    />
                  ) : (
                    <Input
                      type={
                        filter.type === "number" &&
                        filter.useThousandsSeparator
                          ? "text"
                          : filter.type
                      }
                      inputMode={
                        filter.type === "number" ? "numeric" : undefined
                      }
                      value={
                        filter.type === "number" &&
                        filter.useThousandsSeparator
                          ? formatNumericFilterValue(
                              draftFilters[filter.key],
                            )
                          : draftFilters[filter.key] || ""
                      }
                      placeholder={filter.placeholder}
                      dir={filter.type === "text" ? "rtl" : "ltr"}
                      textAlign="center"
                      onChange={(event) =>
                        setDraftFilters((current) => ({
                          ...current,
                          [filter.key]:
                            filter.type === "number" &&
                            filter.useThousandsSeparator
                              ? normalizeNumericFilterValue(event.target.value)
                              : event.target.value,
                        }))
                      }
                    />
                  )}
                </FormControl>
              ))}
            </SimpleGrid>
          </ModalBody>
          <ModalFooter gap={3}>
            <Button variant="ghost" onClick={handleClearFilters}>
              پاک کردن فیلترها
            </Button>
            <Button colorScheme="brand" onClick={handleApplyFilters}>
              اعمال فیلترها
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
